import type CustomWindow from 'interfaces/Utils';
import { useRouter } from 'next/router';
import { UserContext } from 'providers/UserProvider';
import type React from 'react';
import { useContext, useEffect } from 'react';
import type { EventData, GtagEvent, GtagObject, PageViewDataLayerObject } from 'services/Gtm/entities';
import { GTMPageView, pushToDataLayer, trackEvent } from 'services/Gtm/functions';
import { onCLS, onINP, onLCP } from 'web-vitals';
import { GoogleTagManager as GTMComponent } from '@next/third-parties/google';
import InitPageStatus from './InitPageStatus';
import Script from 'next/script';

declare let window: CustomWindow;

export interface PostMessageData {
  type: string;
  trackingData: EventData;
}

export interface PostMessage {
  data: PostMessageData;
}

export interface GoogleTagManagerProps {
  dataLayer: PageViewDataLayerObject;
}

const GoogleTagManager = (props: React.PropsWithChildren<GoogleTagManagerProps>) => {
  const { dataLayer, children } = props;
  const { userState: user } = useContext(UserContext);
  const router = useRouter();

  useEffect(() => {
    const trackWebVitals = ({ name, delta, id }: GtagEvent) => {
      const gtagObject: GtagObject = {
        event: name,
        eventAction: name,
        eventCategory: 'Web Vitals',
        eventLabel: id,
        eventValue: Math.round(name === 'CLS' ? delta * 1000 : delta),
        nonInteraction: 1,
      };
      pushToDataLayer(gtagObject);
    };

    onCLS(trackWebVitals);
    onINP(trackWebVitals);
    onLCP(trackWebVitals);
  }, []);

  useEffect(() => {
    window.addEventListener('message', (e: PostMessage) => {
      if (e.data && e.data.type === 'gtm') {
        trackEvent(e.data.trackingData);
      }
    });

    return () => {
      window.removeEventListener('message', () => {});
    };
  }, []);

  useEffect(() => {
    if (router.asPath) {
      GTMPageView({
        ...dataLayer,
        SubAudienceCD: user.subAudience,
        history: [],
        path: router.asPath,
      });
    }
  }, [router.asPath, dataLayer, user.subAudience]);

  const GTM_ID = process.env.GTM_ID;
  const GTM_AUTH = process.env.GTM_AUTH;
  const GTM_PREVIEW = process.env.GTM_PREVIEW;
  const initDataLayer: { [key: string]: any } = { ...dataLayer };

  return (
    <>
      <Script
        // biome-ignore lint/security/noDangerouslySetInnerHtml: This is a third-party script that needs to be loaded
        dangerouslySetInnerHTML={{
          __html: ` window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('consent', 'default', {
              'ad_storage': 'denied',
              'ad_user_data': 'denied',
              'ad_personalization': 'denied',
              'analytics_storage': 'denied',
              'functionality_storage': 'denied',
              'personalization_storage': 'denied',
              'security_storage': 'granted',
              'wait_for_update': 500
              })`,
        }}
        strategy='beforeInteractive'
        id='gtag-consent-init'
      />
      {GTM_ID && GTM_AUTH && GTM_PREVIEW && (
        <>
          <GTMComponent
            gtmId={GTM_ID}
            auth={GTM_AUTH}
            preview={GTM_PREVIEW}
            dataLayer={initDataLayer}
          />
          <noscript>
            <iframe
              title='fortune-gtm'
              src={`https://www.googletagmanager.com/ns.html?id=${GTM_ID}&gtm_auth=${GTM_AUTH}&gtm_preview=${GTM_PREVIEW}`}
              height='0'
              width='0'
              style={{
                display: 'none',
                visibility: 'hidden',
              }}
            />
          </noscript>
        </>
      )}
      <InitPageStatus />
      {children}
    </>
  );
};

export default GoogleTagManager;
